import { FoodListDivider } from "components/foodItem/FoodListItem/styled"
import MealListItem from "components/foodItem/MealListItem"
import { useSetPageTitle } from "libs/ga"
import { FlexBox } from "components/Common/FlexBox"
import { DotLoading, InfiniteScroll, SpinLoading } from "antd-mobile"
import NoSuggestionMeal from "components/Icons/NoSuggestionMeal"
import { MealType } from "model/food/types"
import useLogMealFromMeal from "features/meal/hooks/logMeal/useLogMealFromMeal"
import { useSetMealTypeParams } from "features/meal/hooks"
import { FavoriteFoodsWrapper, FavoriteMealsContent, Title } from "./styled"
import { generatePath, useNavigate, useOutletContext } from "react-router-dom"
import { FavoriteMealsContextType } from "features/food/foodSlice.types"
import { useSetFavoriteMealDetails } from "../hooks/favoriteMealDetailsHooks"
import PATHS from "router/paths"
import { useEffect } from "react"

const NoSuggestions = () => {
  return (
    <FlexBox
      $direction="column"
      $alignItems="center"
      $justify="center"
      style={{ paddingTop: 48 }}
      $gap={24}
    >
      <NoSuggestionMeal />

      <p style={{ textAlign: "center", color: "#1C1F21", fontSize: 16 }}>
        No favorite food has been added yet.
      </p>
    </FlexBox>
  )
}

const InfinitiScrollContent = ({
  isFirstLoad,
  isLoading,
  hasMore,
}: {
  isFirstLoad: boolean
  isLoading: boolean
  hasMore: boolean
}) => {
  if (isFirstLoad) {
    return (
      <FlexBox $alignItems="center" $justify="center" style={{ height: 200 }}>
        <SpinLoading />
      </FlexBox>
    )
  }

  if (hasMore) {
    return isLoading ? (
      <FlexBox
        style={{ height: 40, width: "100%" }}
        $justify="center"
        $alignItems="center"
      >
        {" "}
        <span>Loading more food</span>
        <DotLoading />
      </FlexBox>
    ) : null
  }

  return "All foods found"
}

const FavoriteMeals = () => {
  useSetPageTitle("Favorite Foods")

  const {
    favoriteMeals,
    getFavoriteMealsLoading,
    isNoFavoriteMeals,
    hasMoreFavoriteMeals,
    loadMoreFavoriteMeals,
    favoriteMealsContentRef,
    setFavoriteMealsScrollTop,
  } = useOutletContext<FavoriteMealsContextType>()

  const navigate = useNavigate()

  const { handleLogMealFromMeal } = useLogMealFromMeal()
  const { mealTypeValue } = useSetMealTypeParams()
  const setFavoriteMealDetails = useSetFavoriteMealDetails()

  // Track scroll position to preserve it when navigating to details
  useEffect(() => {
    const contentElement = favoriteMealsContentRef.current
    if (!contentElement) return

    const handleScroll = () => {
      setFavoriteMealsScrollTop(contentElement.scrollTop)
    }

    contentElement.addEventListener("scroll", handleScroll)

    return () => {
      contentElement.removeEventListener("scroll", handleScroll)
    }
  }, [favoriteMealsContentRef, setFavoriteMealsScrollTop])

  if (isNoFavoriteMeals) {
    return <NoSuggestions />
  }

  const onSelectFavoriteMeal = (meal: MealType) => {
    setFavoriteMealsScrollTop(favoriteMealsContentRef.current?.scrollTop || 0)
    setFavoriteMealDetails(meal)
    navigate(
      generatePath(PATHS.app.favoriteMeal.details, {
        id: meal.id,
      }),
    )
  }

  return (
    <FavoriteFoodsWrapper>
      <FavoriteMealsContent ref={favoriteMealsContentRef}>
        {favoriteMeals.map((meal, index) => (
          <div key={meal.id}>
            {" "}
            <MealListItem
              onSelect={() => onSelectFavoriteMeal(meal)}
              meal={meal}
              onAdd={(meal) => {
                handleLogMealFromMeal({ ...meal, type: mealTypeValue })
              }}
            />
            {index !== favoriteMeals.length - 1 && <FoodListDivider />}
          </div>
        ))}
        <InfiniteScroll
          loadMore={loadMoreFavoriteMeals}
          hasMore={hasMoreFavoriteMeals}
        >
          <InfinitiScrollContent
            hasMore={hasMoreFavoriteMeals}
            isLoading={getFavoriteMealsLoading}
            isFirstLoad={!favoriteMeals.length}
          />
        </InfiniteScroll>
      </FavoriteMealsContent>
    </FavoriteFoodsWrapper>
  )
}

export default FavoriteMeals
